/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import ReactDOM from 'react-dom';
import { GlobalizationProvider, currentCountry, currentLang } from '@rcg/ui-react';
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  from,
} from '@apollo/client';
import { ThemeProvider } from '@material-ui/core';
import { setContext } from '@apollo/client/link/context';
import { applyPolyfills, defineCustomElements } from '@rcg/royal-payway/loader';
import { defineCustomElements as globalLoginDefineCustomElements } from '@rccl/global-login/loader';
import lscache from 'lscache';
import Theme from './utils/theme';
import CelebrityTheme from './utils/theme/celebrity';
import { IS_LOGGED_IN } from './operations/queries';
import { setBookingToken } from './operations/localState/setBookingProfile';
import { getSessionStorageItem, setSessionStorageItem } from './operations/sessionStorage/utils';
import {
  bookingProfileVar,
  selectedDepartureVar,
  selectedReturnVar,
  heldReservationVar,
  bookedReservationVar,
  searchParamsVar,
  paymentVar,
  paramsVar,
  browseParamsVar,
} from './operations/localState/reactiveVariables';
import {
  SESSION_STORAGE_BOOKING_TOKEN_NAME,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_SELECTED_DEPARTURE_FLIGHT,
  SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT,
  SESSION_STORAGE_LAST_HELD_RESERVATION,
  SESSION_STORAGE_LAST_BOOKED_RESERVATION,
  SESSION_STORAGE_LAST_BROWSE_PARAMS,
  SESSION_STORAGE_LAST_SEARCH_PARAMS,
  SESSION_STORAGE_LAST_PAYMENT,
  SESSION_STORAGE_LAST_PARAMS,
  LOCAL_GRAPH_URI,
  BRAND,
  COOKIE_KEYS,
} from './utils/constants';
import { LDUser } from './utils/ldUser';
import { generateRelativeUrl, cacheClean, getCookies } from './utils/common';

import App from './App';

import './styles.css';
import getBrand from './utils/getBrand';

const airCacheKey = 'airFlushed';
const cacheFlushed = lscache.get(airCacheKey);
if (!cacheFlushed) {
  lscache.flush();
  lscache.set(airCacheKey, true);
}

let theme = Theme; // royal default theme
const currentBrand = getBrand();
const isCelebrity = currentBrand.id === BRAND.CELEBRITY;

if (isCelebrity) {
  theme = CelebrityTheme;
}

const {
  REACT_APP_AEM_SERVICE_URL,
  REACT_APP_DICTIONARY_NAME,
  REACT_APP_DICTIONARY_NAME_CELEBRITY,
} = process.env;
const AEM_SERVICE_CACHE_TIME = process.env.REACT_APP_AEM_SERVICE_CACHE_TIME;
const cacheTime = AEM_SERVICE_CACHE_TIME && Number(AEM_SERVICE_CACHE_TIME);

const bookingProfile = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
const lastSelectedDepartureFlight = getSessionStorageItem(
  SESSION_STORAGE_LAST_SELECTED_DEPARTURE_FLIGHT,
);
const lastSelectedReturnFlight = getSessionStorageItem(SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT);
const lastBrowseParams = getSessionStorageItem(SESSION_STORAGE_LAST_BROWSE_PARAMS);
const lastSearchParams = getSessionStorageItem(SESSION_STORAGE_LAST_SEARCH_PARAMS);
const bookingToken = getSessionStorageItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, false);
const heldReservation = getSessionStorageItem(SESSION_STORAGE_LAST_HELD_RESERVATION);
const bookedReservation = getSessionStorageItem(SESSION_STORAGE_LAST_BOOKED_RESERVATION);
const payment = getSessionStorageItem(SESSION_STORAGE_LAST_PAYMENT);
const params = getSessionStorageItem(SESSION_STORAGE_LAST_PARAMS);

if (bookingProfile && bookingProfile.bookingToken === bookingToken) {
  bookingProfileVar(bookingProfile);
  selectedDepartureVar(lastSelectedDepartureFlight);
  selectedReturnVar(lastSelectedReturnFlight);
  heldReservationVar(heldReservation);
  bookedReservationVar(bookedReservation);
  browseParamsVar(lastBrowseParams);
  searchParamsVar(lastSearchParams);
  paymentVar(payment);
  paramsVar(params);
} else {
  setBookingToken(bookingToken);
  setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
  setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
}
cacheClean();
const eslEnvLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    sessionStorage.removeItem('eslEnv');
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;
    if (headers) {
      const eslEnvHeader = headers.get('esl-env');
      if (eslEnvHeader && eslEnvHeader !== null) {
        sessionStorage.setItem('eslEnv', eslEnvHeader);
      }
    }
    return response;
  });
});
const httpLink = createHttpLink({
  uri: generateRelativeUrl(process.env.REACT_APP_GRAPHQL_URI) || LOCAL_GRAPH_URI,
});
function getAuthToken(inputToken) {
  return inputToken.substring(7);
}

const authLink = setContext((_, { headers }) => {
  const guestAcctAccessToken = getCookies(COOKIE_KEYS.ACCESS_TOKEN);
  const newHeaders = {
    'apollographql-client-name': 'Air',
    'apollographql-query-name': _.operationName,
    country: currentCountry().toUpperCase(),
    language: currentLang(),
    'x-session-id': LDUser(),
    ...headers,
  };

  if (guestAcctAccessToken) {
    newHeaders['x-guest-acct-access-token'] = guestAcctAccessToken;
  }

  newHeaders[SESSION_STORAGE_BOOKING_TOKEN_NAME] =
    getSessionStorageItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, false) ||
    getAuthToken(window.location.search).split('&')[0];

  return { headers: newHeaders };
});

const cache = new InMemoryCache();

cache.writeQuery({
  query: IS_LOGGED_IN,
  data: {
    isLoggedIn: !!bookingToken,
  },
});

const client = new ApolloClient({
  link: from([authLink, eslEnvLink, httpLink]),
  clientState: {},
  resolvers: {},
  cache,
});

ReactDOM.render(
  <GlobalizationProvider
    url={generateRelativeUrl(REACT_APP_AEM_SERVICE_URL)}
    cacheTime={cacheTime}
    dicName={isCelebrity ? REACT_APP_DICTIONARY_NAME_CELEBRITY : REACT_APP_DICTIONARY_NAME}>
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </ApolloProvider>
  </GlobalizationProvider>,
  document.getElementById('root'),
);

/*
  Royal payway implementation
  More info: https://confluence.rccl.com/pages/viewpage.action?spaceKey=PAYM&title=Web+Environment
  3DS Component: https://confluence.rccl.com/display/PAYM/3DS+Component
*/
applyPolyfills().then(() => {
  defineCustomElements();
});

globalLoginDefineCustomElements(window);
